import { useState, useMemo } from 'react';
import {
  Alert,
  Button,
  Chip,
  CloseIcon,
  DomainIcon,
  PageHeader,
  Select,
  SelectItem,
  SelectTrigger,
  Spinner,
  Text,
  ledgerContainerClass,
} from '@billda/design-system';
import type { VariationStatus } from '@billda/api';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { SidePanel, ResizableHandle } from '../../../shared/components/panel';
import { BookingDetailsPanel } from '../components/panel/organisms/BookingDetailsPanel';
import { useVariationsList } from '../hooks/useVariationsList';
import { useSitesSummary } from '../hooks/useSitesSummary';
import { useSitePanelState } from '../hooks/useSitePanelState';
import { useVariationsUIStore } from '../stores/variationsUIStore';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { VariationsLedger } from '../variations-ledger/VariationsLedger';
import { VARIATIONS_COLLAPSE } from '../variations-ledger/columns';
import {
  toVariationRows,
  type VariationLedgerRowModel,
} from '../variations-ledger/toVariationRows';

const STATUS_TABS = [
  { label: 'All', value: '' },
  { label: 'Booked', value: 'Booked' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
] as const;

const VariationsPage = () => {
  const { siteIdFilter, setSiteIdFilter, statusFilter, setStatusFilter } =
    useVariationsUIStore();
  const {
    isOpen,
    id: panelId,
    type,
    closePanel,
    openPanel,
  } = useSitePanelState();
  const { data: sites } = useSitesSummary();
  // Track only what the user has explicitly collapsed — so every week is
  // expanded by default, including weeks arriving with the next page.
  const [collapsedWeeks, setCollapsedWeeks] = useState<ReadonlySet<string>>(
    new Set()
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useVariationsList(siteIdFilter, statusFilter);

  const allItems = useMemo(
    () => data?.pages.flatMap(p => p.items) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;
  const weeks = useMemo(() => toVariationRows(allItems), [allItems]);

  const toggleWeek = (key: string) =>
    setCollapsedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const handleRowClick = (row: VariationLedgerRowModel) => {
    openPanel(
      'variation',
      row.id,
      row.siteId.toString(),
      row.plotId.toString()
    );
  };

  const handleSiteFilterChange = (value: string) => {
    setSiteIdFilter(value ? parseInt(value, 10) : undefined);
  };

  const selectedSiteName = siteIdFilter
    ? sites?.find(s => s.id === siteIdFilter)?.name
    : undefined;

  const isFilterActive = !!siteIdFilter;

  const filterContent = (
    <div>
      <Text
        variant="caption"
        color="secondary"
        className="mb-1.5 block font-medium"
      >
        Site
      </Text>
      <Select
        value={siteIdFilter?.toString() ?? ''}
        onValueChange={value => handleSiteFilterChange(value)}
      >
        <SelectTrigger className="h-8" data-testid="site-filter-select" />
        <SelectItem value="">All sites</SelectItem>
        {sites?.map(site => (
          <SelectItem key={site.id} value={site.id.toString()}>
            {site.name}
          </SelectItem>
        ))}
      </Select>
    </div>
  );

  const filterChips =
    siteIdFilter && selectedSiteName ? (
      <Chip
        size="sm"
        icon={<DomainIcon className="size-3.5" aria-hidden="true" />}
        label={
          <span className="flex items-center gap-1">
            <Text variant="caption" as="span" className="font-medium">
              Site
            </Text>
            <Text variant="caption" as="span" color="secondary">
              is
            </Text>
            <Text variant="caption" as="span" className="font-medium">
              {selectedSiteName}
            </Text>
          </span>
        }
        onDelete={() => setSiteIdFilter(undefined)}
        deleteIcon={<CloseIcon className="size-3.5" aria-hidden="true" />}
        className="h-6.5 rounded-lg"
      />
    ) : undefined;

  const statusTabs = (
    <ChipTabs
      tabs={[...STATUS_TABS]}
      value={statusFilter ?? ''}
      onChange={v => setStatusFilter((v as VariationStatus) || undefined)}
      data-testid="variations-status-tabs"
    />
  );

  const panelContent = (
    <>
      {isOpen && panelId && type === 'variation' && (
        <>
          <ResizableHandle />
          <SidePanel open={isOpen} onClose={closePanel} title="Booking details">
            <BookingDetailsPanel variationId={panelId} />
          </SidePanel>
        </>
      )}
    </>
  );

  const noItems = allItems.length === 0 && !isLoading;
  const hasFilters = isFilterActive || !!statusFilter;

  return (
    <PageContentArea isPanelOpen={isOpen} panel={panelContent}>
      <PageHeader title="Variations" />
      <PageToolbar
        tabs={statusTabs}
        filterContent={filterContent}
        filterChips={filterChips}
        isFilterActive={isFilterActive}
      />

      <div
        className={`flex-1 overflow-y-auto ${ledgerContainerClass(VARIATIONS_COLLAPSE.container)}`}
      >
        {isLoading && !data ? (
          <div className="flex min-h-50 items-center justify-center p-6">
            <Spinner />
          </div>
        ) : error ? (
          <Alert severity="error" className="m-4">
            {(error as Error).message}
          </Alert>
        ) : noItems && !hasFilters ? (
          <div className="p-8 text-center">
            <Text variant="body1" color="secondary" className="mb-2">
              No variations yet
            </Text>
            <Text variant="body2" color="secondary">
              Variations booked from the mobile app will appear here.
            </Text>
          </div>
        ) : noItems ? (
          <div className="p-8 text-center">
            <Text variant="body1" color="secondary" className="mb-2">
              No variations match these filters
            </Text>
            <Text variant="body2" color="secondary" className="mb-4">
              Try different filters or clear them to see all variations.
            </Text>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSiteIdFilter(undefined);
                setStatusFilter(undefined);
              }}
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <VariationsLedger
            weeks={weeks}
            collapsedWeeks={collapsedWeeks}
            onToggleWeek={toggleWeek}
            onOpenRow={handleRowClick}
            totalCount={totalCount}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
          />
        )}
      </div>
    </PageContentArea>
  );
};

export default VariationsPage;
